import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getMaxPointSize } from '../scene-manager.js';
import {
  laserClampChunk,
  laserBillboardChunk,
  laserBeamFragmentShader,
  laserImpactFragmentShader,
  laserDustFragmentShader
} from '../lighting/laser-array.js';

// Shared-rig contract (see the framing addendum). 'forbidden': a laser show
// is played on a black stage. The rig's wash cones flood the frame and
// destroy exactly the contrast the beams depend on to read as light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'lasercathedral',
  family: 'stage',
  energyLevel: 0.90,
  bpmMin: 124,
  bpmMax: 190,
  movement: 0.90,
  danceability: 0.80,
  tonality: 0.55,
  enabled: true
};

// LaserCathedral: a big-room laser show - Eric Prydz / Excision / Street
// Parade tunnel / Jarre territory. Three projectors, sixteen beams each,
// cycling through the gestures a real show actually uses: the SHEET (dozens
// of coplanar beams seen edge-on, reading as one solid blade of light), the
// TUNNEL (a rotating cone aimed at the audience), the quantised floor SCAN
// with its impact dots, and the static vertical WALL.
//
// The one rule that makes or breaks the illusion: a laser is MONOCHROMATIC.
// One pure colour per projector, never a hue gradient inside a beam. Every
// beam fragment here takes its projector's single colour and only ever
// varies in brightness.
//
// PERFORMANCE SHAPE (fill-rate bound on VideoCore VI / Mali):
//  - ONE draw call for all beams (InstancedBufferGeometry, 2 triangles per
//    beam), one for the fan sheet (3 instances), one for the ground impacts.
//  - Zero per-frame attribute uploads. Every beam's DIRECTION is derived in
//    the vertex shader from the current mode + uTime + its own index, so
//    update() writes only scalar uniforms.
//  - mediump fragment precision, no discard, no texture, no noise.
//  - The CONE mode aims beams at the camera, where a billboarded quad seen
//    end-on would otherwise blow up to cover a quarter of the screen and
//    its side vector would degenerate - see uWidthClamp handling below.

const BEAM_LENGTH = 26;
const BEAM_HALF_WIDTH_NEAR = 0.035 / 2; // real lasers diverge slightly; this
const BEAM_HALF_WIDTH_FAR = 0.075 / 2; // is a large part of why they read as real
// Screen-space floor on the beam width. World units alone collapse to a few
// pixels at stage distance.
const BEAM_OUTER_PX = 18.0;
const BEAM_CORE_PX = 3.0;
const PROJECTOR_COUNT = 3;

// P0 left, P1 centre-high, P2 right.
//
// P1 sits directly behind the DOM cover overlay, which is centred on screen -
// at y 7.4 its source sprite and halo landed about 39% of the way up the
// half-frame and the cover hid them. Raised to clear it: 9.6 puts the head at
// roughly 58% up, well above the overlay, and its beams still fan down through
// the same volume because only the apex moved.
const PROJECTORS = [
  [-4.6, 6.2, -6.5],
  [0, 9.6, -8.5],
  [4.6, 6.2, -6.5]
];

const CAMERA_POS = [0, 2.2, 8.5];
const CAMERA_LOOK = [0, 2.6, -1];
const FOG_DENSITY = 0.055; // haze is what makes a beam visible at all
const FLOOR_SIZE = 60;
const FLOOR_COLOR = 0x0a0c10; // near-black: a laser plot, not a lit stage

// Modes. Kept as plain ints for a uniform branch in the vertex shader.
const MODE_FAN = 0;
const MODE_CONE = 1;
const MODE_SCAN = 2;
const MODE_WALL = 3;
const MODE_BURST = 4;
const CYCLE_MODES = [MODE_FAN, MODE_CONE, MODE_SCAN, MODE_WALL];
const BURST_PROBABILITY = 1 / 8;
const BURST_BEATS = 8; // exactly 2 bars
// No-bpm fallback only - accentPhrase (director.js) needs a beat grid, so
// without bpm this file falls back to its own wall clock instead, same as
// it always has.
const MODE_NO_BPM_SECONDS_MIN = 12;
const MODE_NO_BPM_SECONDS_MAX = 20;
const BLACKOUT_FRAMES = 2; // a real laser show cuts, it never crossfades

const BEAT_PULSE_AMOUNT = 0.7;
const BEAT_PULSE_DECAY = 14; // high on purpose: a laser cracks, it does not breathe

const SCAN_STEP_RAD = 0.13; // one quantised notch per beat

const PEAK_LUMINANCE = 0.92;
// Only the hue is taken from the cover - see _applyPalette. These pin every
// beam to a genuinely saturated laser colour regardless of the artwork.
// Floors and a range rather than fixed values: a laser stays saturated, but a
// pastel sleeve and a fluorescent one should not render identically.
const LASER_MIN_SATURATION = 0.80;
const LASER_MIN_LIGHTNESS = 0.45;
const LASER_MAX_LIGHTNESS = 0.60;
// Half-spacing of the analogous triad, driven by the cover's own hue spread.
const TRIAD_DELTA_MIN = 0.09;
const TRIAD_DELTA_MAX = 0.17;
const FALLBACK_COLORS = [0xff2020, 0x20ff40, 0x2060ff];

// impacts (floor light pools + reflection) share the same instanced beam
// buffer every other layer already uses, sized by perProjector - at tier 0
// that buffer is already down to 8*PROJECTOR_COUNT instances, so drawing
// impacts+reflection on top of it costs two cheap extra draw calls, not a
// meaningful chunk of the tier's own frame budget. Keeping it on at every
// tier avoids losing the floor entirely on weaker hardware (TVs) for
// savings that were never where tier 0's real cost was (perProjector/sheet/
// dustCount are the actual levers).
const TIER_LAYOUT = {
  0: { perProjector: 8, sheet: false, impacts: true, dustCount: 300 },
  1: { perProjector: 12, sheet: true, impacts: true, dustCount: 800 },
  2: { perProjector: 16, sheet: true, impacts: true, dustCount: 800 }
};
const DEFAULT_TIER = 1;

const IMPACT_MAX_RADIUS = 12;
const IMPACT_BASE_SIZE = 0.30; // a laser hit is a small hard dot, not a broad wash

// WALL and SCAN are the two gesture types that leave the frame emptiest - a
// near-parallel curtain or a single quantised sweep never fills the width the
// way FAN/CONE/BURST's full-aperture spread does. For these two modes only,
// the buffer is over-allocated so more beams can switch on without a second
// geometry: the RATIO sets how many extra slots per tier (tier2's 16
// becomes 24, matching the brief's own example), and the WIDE multiplier
// pushes the beams' own spread wide enough to exit the frame left/right.
const WALL_SCAN_BEAM_RATIO = 1.5; // 16 -> 24
const WALL_SCAN_WIDE_MULTIPLIER = 1.6;

// --- Enrichment: projector sources (glow point + halo) ----------------------
// Housing boxes (a small opaque MeshStandardMaterial cube at each origin)
// were tried and removed: at this scale they read as a flat cyan square, an
// artefact rather than a fixture. The point of origin is meant to be a place
// light comes FROM, not an object - no opaque geometry, no straight edges.
const SOURCE_HALO_PX = 35;
const SOURCE_CORE_PX = 5;
const SOURCE_HALO_OPACITY = 0.15;

// --- Enrichment: floor reflection --------------------------------------------
const REFLECTION_STRETCH = 4.0; // vs the impact's own IMPACT_BASE_SIZE
const REFLECTION_OPACITY = 0.18;

// --- Enrichment: fog dust revealed by the beams ------------------------------
// Point/line distance against a fixed set of representative beam lines (one
// technique instance per projector spread position), not against all
// instanced beams - 800 particles x 8 lines is cheap, 800 x up to 48 beams
// would not be. Reprises particle-storm.js's StormLayer structure (slow
// drift, additive point sprites, reveal-driven brightness) with distance
// measured to a LINE instead of a fixed target point, since a laser beam is
// a ray through the volume, not a point on the floor.
const DUST_TEST_BEAM_COUNT = 8;
// A beam is a 1D line through a 3D field: at the spec's own 0.42, the swept
// reveal volume against 800 randomly-placed particles worked out to only
// ~4-5 visibly lit motes in a full-frame FAN shot - correct per the letter of
// "distance to the line", but nowhere near enough to read as "matter the
// beam passes through" in the verification capture. Wide enough now that the
// revealed volume is a soft glow tube around each beam rather than isolated
// sparks - which is also closer to how haze actually photographs.
const DUST_REVEAL_RADIUS = 0.85;
const DUST_POINT_WORLD_SIZE = 0.09;
const DUST_POINT_WORLD_VARIANCE = 0.05;
const DUST_BRIGHTNESS = 1.9;
// The volume the beams actually travel through: roughly the projectors'
// footprint down to the floor, and from just behind them out to the camera.
const DUST_FIELD = { halfX: 8.5, yMin: 0.3, yMax: 9.4, zMin: -9, zMax: 6 };

// The background gradient tried here was removed: a near-imperceptible radial
// quad still left a visible horizontal seam at mid-height (its own edge, not
// the floor's), and a laser show reads as a black stage - pure 0x000000
// (scene-manager's own clear colour, never overridden here) IS the correct
// backdrop, not a decorative approximation of one.


// Clamping a colour PER CHANNEL collapses its hue: a saturated red pushed
// bright becomes (0.92, 0.92, 0.5) once two channels hit the ceiling, i.e.
// white-ish. Scaling the whole colour by one factor keeps the channel ratio -
// and therefore the hue - exactly intact at any brightness, which is what
// "a laser is monochromatic" actually requires.
// Both chunks now come from the shared LaserArray module (extracted from this
// very file so laser-grid.js could reuse them) - same text, same numbers.
const CLAMP_CHUNK = laserClampChunk(PEAK_LUMINANCE);
const BILLBOARD_CHUNK = laserBillboardChunk({
  halfWidthNear: BEAM_HALF_WIDTH_NEAR,
  halfWidthFar: BEAM_HALF_WIDTH_FAR
});

// Shared by the beam, sheet and impact shaders: rebuilds a beam's direction
// from nothing but its projector, its index and the current mode/time. This
// is what keeps update() free of any per-instance CPU work.
const BEAM_DIR_CHUNK = `
  uniform float uMode;
  uniform float uTime;
  uniform float uSpread;      // fan half-angle / cone half-angle, progress driven
  uniform float uScanAngle;   // quantised, advanced one notch per beat
  uniform float uWideBeams;   // >1 during WALL/SCAN only - widens their spread
  uniform vec3 uProjector0;
  uniform vec3 uProjector1;
  uniform vec3 uProjector2;

  vec3 projectorOrigin(float p) {
    if (p < 0.5) return uProjector0;
    if (p < 1.5) return uProjector1;
    return uProjector2;
  }

  // Any unit vector perpendicular to n, chosen so it never degenerates.
  vec3 anyPerp(vec3 n) {
    vec3 a = abs(n.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    return normalize(cross(n, a));
  }

  // frac01: this beam's position across its projector's spread, in [-1, 1].
  vec3 beamDirection(float p, float frac01, float seed) {
    vec3 origin = projectorOrigin(p);
    // Everything aims from the truss down and out toward the audience (+Z).
    // NOT toward +Z: the camera sits at z=+8.5, so aiming there pointed every
    // beam straight down the lens - the width guard then cancelled them and
    // every impact landed at the same depth, in one flat row. This drops the
    // beams onto the deck around z=-2.5, in front of the projectors.
    vec3 fwd = normalize(vec3(0.0, -0.55, 0.35));
    vec3 right = normalize(cross(fwd, vec3(0.0, 1.0, 0.0)));
    vec3 up = normalize(cross(right, fwd));

    if (uMode < 0.5) {
      // FAN - coplanar, evenly spaced, the whole plane rolling slowly.
      float a = frac01 * uSpread;
      float roll = uTime * 0.22 + p * 1.7;
      vec3 axis = right * cos(roll) + up * sin(roll);
      return normalize(fwd * cos(a) + axis * sin(a));
    } else if (uMode < 1.5) {
      // CONE - axis aimed at the audience, spinning on itself.
      // Aimed at the audience but 3 units BELOW the lens, so the cone passes
      // under the camera instead of firing into it.
      vec3 axis = normalize((cameraPosition - vec3(0.0, 3.0, 0.0)) - origin);
      vec3 r = anyPerp(axis);
      vec3 u = normalize(cross(axis, r));
      float phi = frac01 * 3.14159265 + uTime * 0.9 + p * 2.1;
      float half_ = uSpread * 0.85;
      return normalize(axis * cos(half_) + (r * cos(phi) + u * sin(phi)) * sin(half_));
    } else if (uMode < 2.5) {
      // SCAN - swept across the floor in quantised notches, not continuously.
      // uWideBeams widens the notch's own angular spread so a busier SCAN
      // actually reaches the frame's edges instead of staying centred.
      float az = uScanAngle + frac01 * uSpread * 1.3 * uWideBeams + p * 0.35;
      return normalize(vec3(sin(az) * 0.9, -1.0, cos(az) * 0.55 + 0.45));
    } else if (uMode < 3.5) {
      // WALL - a steep near-parallel curtain, converging very slowly, with a
      // slow lateral sweep of the WHOLE wall: the sweep term is NOT scaled by
      // frac01, so it shifts every beam by the same amount rather than
      // widening the fan - the curtain pans left-right as one rigid sheet.
      float conv = 0.12 + 0.08 * sin(uTime * 0.15);
      float sweep = sin(uTime * 0.05) * 0.4;
      return normalize(vec3(frac01 * conv * 3.2 * uWideBeams + sweep, -1.0, 0.28));
    }
    // BURST - maximum aperture, projectors strobing in alternation.
    float a = frac01 * 1.35;
    float roll = uTime * 1.6 + p * 2.094;
    vec3 axis = right * cos(roll) + up * sin(roll);
    return normalize(fwd * cos(a) + axis * sin(a));
  }

  // 1 while this projector is lit. Only BURST gates: the others stay on.
  float projectorGate(float p) {
    if (uMode < 3.5) return 1.0;
    float slot = floor(mod(uTime * 9.0, 3.0));
    return abs(slot - p) < 0.5 ? 1.0 : 0.0;
  }
`;

const beamVertexShader = `
  precision highp float;

  attribute float iProjector;
  attribute float iIndex;
  attribute float iSeed;
  uniform float uBeamsPerProjector;
  uniform float uMasterGain;

  varying float vSide;
  varying float vLen;
  varying vec3 vColor;
  varying float vSeed;
  varying float vGain;
  varying float vSafeAtt;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${BEAM_DIR_CHUNK}
  ${BILLBOARD_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}

  vec3 projectorColor(float p) {
    if (p < 0.5) return uColor0;
    if (p < 1.5) return uColor1;
    return uColor2;
  }

  void main() {
    float frac01 = uBeamsPerProjector > 1.0
      ? (iIndex / (uBeamsPerProjector - 1.0)) * 2.0 - 1.0
      : 0.0;
    // The buffer is allocated for the LARGEST per-projector count any mode
    // needs (WALL/SCAN); instances beyond the count the CURRENT mode is
    // actually using sit idle rather than folding back into the active
    // range - uBeamsPerProjector itself changes per mode (see update()).
    float activeGate = step(iIndex, uBeamsPerProjector - 0.5);
    vec3 origin = projectorOrigin(iProjector);
    vec3 dir = beamDirection(iProjector, frac01, iSeed);

    float t = position.y;                 // 0 at the source, 1 at the far end
    vec3 along = origin + dir * (t * ${BEAM_LENGTH.toFixed(1)});

    // Billboarding, the pixel width floor and the camera-axis guard all live
    // in the shared LaserArray chunk - see lighting/laser-array.js, where the
    // original comments moved with the code.
    float guard;
    vec3 worldPos = laserBillboard(along, dir, t, position.x, guard);

    vSide = position.x;
    vLen = t;
    vSeed = iSeed;
    vColor = projectorColor(iProjector);
    vGain = uMasterGain * projectorGate(iProjector) * guard * activeGate;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;

// Verbatim from this file, now produced by the shared factory - 0.85 is the
// hot-core mix this visualizer has always used.
const beamFragmentShader = laserBeamFragmentShader({ hotMix: 0.85, peakLuminance: PEAK_LUMINANCE });

// The sheet: one low-opacity quad per projector spanning its whole fan, which
// is what actually sells "a solid blade of light" rather than 16 separate
// lines. Only shown in the two fan-shaped modes.
const sheetVertexShader = `
  precision highp float;

  attribute float iProjector;
  uniform float uMasterGain;

  varying float vLen;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafeAtt;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${BEAM_DIR_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}

  vec3 projectorColor(float p) {
    if (p < 0.5) return uColor0;
    if (p < 1.5) return uColor1;
    return uColor2;
  }

  void main() {
    // position.x = -1 / +1 picks the two extreme edges of the fan, so the
    // quad exactly fills the sector the beams sweep.
    vec3 origin = projectorOrigin(iProjector);
    vec3 dir = beamDirection(iProjector, position.x, 0.0);
    float t = position.y;
    vec3 worldPos = origin + dir * (t * ${BEAM_LENGTH.toFixed(1)});

    vLen = t;
    vColor = projectorColor(iProjector);
    // Hidden outside the fan-shaped modes (FAN and BURST).
    float modeOn = (uMode < 0.5 || uMode > 3.5) ? 1.0 : 0.0;
    vGain = uMasterGain * projectorGate(iProjector) * modeOn;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;

const sheetFragmentShader = `
  precision mediump float;

  uniform float uPulse;

  varying float vLen;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafeAtt;

  ${CLAMP_CHUNK}

  void main() {
    float falloff = smoothstep(1.0, 0.35, vLen);
    float brightness = 0.055 * falloff * uPulse * vGain * vSafeAtt;
    // See the beam shader: all intensity in rgb, alpha 1 - additive blending
    // already multiplies by alpha.
    gl_FragColor = vec4(clampLuminance(vColor * brightness), 1.0);
  }
`;

// Ground impacts: where each beam actually meets y=0. Analytic ray/plane
// intersection from the same direction function, so they can never drift out
// of sync with the beams.
const impactVertexShader = `
  precision highp float;

  attribute float iProjector;
  attribute float iIndex;
  attribute float iSeed;
  uniform float uBeamsPerProjector;
  uniform float uMasterGain;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${BEAM_DIR_CHUNK}

  vec3 projectorColor(float p) {
    if (p < 0.5) return uColor0;
    if (p < 1.5) return uColor1;
    return uColor2;
  }

  void main() {
    float frac01 = uBeamsPerProjector > 1.0
      ? (iIndex / (uBeamsPerProjector - 1.0)) * 2.0 - 1.0
      : 0.0;
    float activeGate = step(iIndex, uBeamsPerProjector - 0.5);
    vec3 origin = projectorOrigin(iProjector);
    vec3 dir = beamDirection(iProjector, frac01, iSeed);

    // Only beams actually travelling downward can land on the floor.
    float denom = dir.y;
    float t = denom < -0.0001 ? -origin.y / denom : -1.0;
    vec3 hit = origin + dir * max(t, 0.0);
    float within = step(0.0, t) * step(length(hit.xz), ${IMPACT_MAX_RADIUS.toFixed(1)});

    // A grazing beam stretches its pool along ONE axis - the direction the
    // beam is travelling across the floor - while its width stays the beam's
    // own. Scaling the patch uniformly (as a camera-facing sprite must) is
    // simply the wrong shape for a light pool.
    float incidence = clamp(abs(denom), 0.15, 1.0);
    vec2 gxz = dir.xz;
    float gLen = length(gxz);
    // A perfectly vertical beam has no floor direction; any axis will do.
    vec2 g = gLen > 0.0001 ? gxz / gLen : vec2(1.0, 0.0);
    vec3 major = vec3(g.x, 0.0, g.y);
    vec3 minor = vec3(-g.y, 0.0, g.x);
    float majorLen = ${IMPACT_BASE_SIZE.toFixed(3)} / incidence;
    float minorLen = ${IMPACT_BASE_SIZE.toFixed(3)};

    vec3 worldPos = hit
                  + major * (position.x * majorLen)
                  + minor * (position.z * minorLen);
    worldPos.y = 0.03; // just clear of the floor plane

    vUv = position.xz;
    vColor = projectorColor(iProjector);
    vGain = uMasterGain * projectorGate(iProjector) * within * activeGate;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
  }
`;

const impactFragmentShader = laserImpactFragmentShader({ hotMix: 0.8, peakLuminance: PEAK_LUMINANCE });

// ---------------------------------------------------------------------------
// Enrichment - floor reflection: a second set of ground-impact quads, stretched
// along the CAMERA-TO-HIT axis rather than the beam's own travel direction.
// A glossy floor's highlight elongates toward the VIEWER regardless of which
// way the light itself is travelling - that is what makes it read as a
// reflection rather than a second, oddly-shaped impact. Halo only, no white
// core: a reflection is a soft smear, never a hot point.
// ---------------------------------------------------------------------------
const reflectionVertexShader = `
  precision highp float;

  attribute float iProjector;
  attribute float iIndex;
  attribute float iSeed;
  uniform float uBeamsPerProjector;
  uniform float uMasterGain;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${BEAM_DIR_CHUNK}

  vec3 projectorColor(float p) {
    if (p < 0.5) return uColor0;
    if (p < 1.5) return uColor1;
    return uColor2;
  }

  void main() {
    float frac01 = uBeamsPerProjector > 1.0
      ? (iIndex / (uBeamsPerProjector - 1.0)) * 2.0 - 1.0
      : 0.0;
    float activeGate = step(iIndex, uBeamsPerProjector - 0.5);
    vec3 origin = projectorOrigin(iProjector);
    vec3 dir = beamDirection(iProjector, frac01, iSeed);

    float denom = dir.y;
    float t = denom < -0.0001 ? -origin.y / denom : -1.0;
    vec3 hit = origin + dir * max(t, 0.0);
    float within = step(0.0, t) * step(length(hit.xz), ${IMPACT_MAX_RADIUS.toFixed(1)});

    vec2 toCam = cameraPosition.xz - hit.xz;
    float toCamLen = length(toCam);
    vec2 g = toCamLen > 0.0001 ? toCam / toCamLen : vec2(1.0, 0.0);
    vec3 major = vec3(g.x, 0.0, g.y);
    vec3 minor = vec3(-g.y, 0.0, g.x);
    float majorLen = ${IMPACT_BASE_SIZE.toFixed(3)} * ${REFLECTION_STRETCH.toFixed(1)};
    float minorLen = ${IMPACT_BASE_SIZE.toFixed(3)};

    vec3 worldPos = hit
                  + major * (position.x * majorLen)
                  + minor * (position.z * minorLen);
    // Just under the impact pools (0.03), still clear of the floor plane.
    worldPos.y = 0.02;

    vUv = position.xz;
    vColor = projectorColor(iProjector);
    vGain = uMasterGain * projectorGate(iProjector) * within * activeGate;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
  }
`;

const reflectionFragmentShader = `
  precision mediump float;

  uniform float uPulse;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  ${CLAMP_CHUNK}

  void main() {
    float r = length(vUv);
    float h1 = max(1.0 - r, 0.0);
    float halo = h1 * h1 * h1;
    vec3 col = vColor * halo * ${REFLECTION_OPACITY.toFixed(2)};
    col *= uPulse * vGain;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// ---------------------------------------------------------------------------
// Enrichment - projector sources: a bright near-white point plus a soft
// tinted halo, combined into ONE point sprite so the pair costs a single
// draw call. The core is what actually crosses the bloom threshold (0.78) -
// mixing 95% toward white pushes a saturated laser colour's luminance well
// past it - and gives every beam a physical origin instead of starting from
// nothing.
// ---------------------------------------------------------------------------
const sourceVertexShader = `
  precision highp float;

  attribute float iIndex; // which projector (0/1/2) - also doubles as colour pick
  // uSourceSizeScale is canvasPixelHeight/1080 - a FIXED screen size, not a
  // world size. uPixelScale (used by the beams for their width floor) is
  // pixels-per-WORLD-UNIT at unit depth; multiplying a target PIXEL count by
  // it and dividing by view depth - what this shader did originally - treats
  // the pixel count as if it were a world size, and at the projectors' own
  // ~16-unit depth that inflated a "35px" halo to roughly 1750px: a soft
  // wash covering most of the frame, easily mistaken for a background layer.
  uniform float uSourceSizeScale;
  uniform float uPointSizeMax;

  varying vec3 vColor;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  vec3 projectorColorS(float p) {
    if (p < 0.5) return uColor0;
    if (p < 1.5) return uColor1;
    return uColor2;
  }

  void main() {
    vColor = projectorColorS(iIndex);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = clamp(${SOURCE_HALO_PX.toFixed(1)} * uSourceSizeScale, 1.0, uPointSizeMax);
    gl_Position = projectionMatrix * mv;
  }
`;

const sourceFragmentShader = `
  precision mediump float;

  varying vec3 vColor;

  ${CLAMP_CHUNK}

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float r = length(uv) * 2.0;
    if (r > 1.0) discard;
    float coreFrac = ${(SOURCE_CORE_PX / SOURCE_HALO_PX).toFixed(4)};
    float core = 1.0 - smoothstep(0.0, coreFrac, r);
    float halo = (1.0 - r) * (1.0 - r);
    vec3 hot = mix(vColor, vec3(1.0), 0.95);
    vec3 col = vColor * halo * ${SOURCE_HALO_OPACITY.toFixed(2)} + hot * core;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// ---------------------------------------------------------------------------
// Enrichment - fog dust: point/LINE distance against a fixed set of eight
// representative beam lines (not all instanced beams - 800 particles x 8
// lines is cheap, x up to 48 would not be). Same shape as particle-
// storm.js's StormLayer (slow drift, additive point sprites, reveal-driven
// brightness), but the reveal test is a distance to a ray through the
// volume rather than to a fixed floor target, since a laser beam is a line
// in 3D, not a point on the ground.
// ---------------------------------------------------------------------------
const dustVertexShader = `
  precision highp float;

  attribute float aSeed;
  // uTime, uMode, uSpread, uScanAngle and uProjector0/1/2 all come from
  // BEAM_DIR_CHUNK below - redeclaring uTime here as well is what actually
  // broke this shader the first time (a duplicate uniform declaration is a
  // straight compile error, unlike the precision-mismatch case elsewhere in
  // this file where the SAME name differs only in precision between stages).
  uniform float uPixelScale;
  uniform float uPointSizeMax;
  uniform float uDustRadius;
  uniform float uMasterGain;

  varying float vReveal;
  varying vec3 vColor;

  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;

  ${BEAM_DIR_CHUNK}

  vec3 projectorColorD(float p) {
    if (p < 0.5) return uColor0;
    if (p < 1.5) return uColor1;
    return uColor2;
  }

  // Eight fixed (projector, spread-fraction) test points, spanning each
  // projector's own fan/cone/scan/wall range - not the actual per-instance
  // beam indices, which can number in the dozens.
  vec2 testBeam(int i) {
    if (i == 0) return vec2(0.0, -1.0);
    if (i == 1) return vec2(0.0, 0.0);
    if (i == 2) return vec2(0.0, 1.0);
    if (i == 3) return vec2(1.0, -1.0);
    if (i == 4) return vec2(1.0, 0.0);
    if (i == 5) return vec2(1.0, 1.0);
    if (i == 6) return vec2(2.0, -1.0);
    return vec2(2.0, 1.0);
  }

  void main() {
    vec3 pos = position;
    pos.y += sin(uTime * 0.5 + aSeed * 6.2831) * 0.18;
    pos.x += sin(uTime * 0.3 + aSeed * 4.0) * 0.15;
    pos.z += cos(uTime * 0.25 + aSeed * 3.1) * 0.15;

    float best = 0.0;
    vec3 bestColor = vec3(0.0);
    for (int i = 0; i < ${DUST_TEST_BEAM_COUNT}; i++) {
      vec2 tb = testBeam(i);
      vec3 origin = projectorOrigin(tb.x);
      vec3 dir = beamDirection(tb.x, tb.y, 0.0);
      vec3 op = pos - origin;
      float t = clamp(dot(op, dir), 0.0, ${BEAM_LENGTH.toFixed(1)});
      vec3 closest = origin + dir * t;
      float d = distance(pos, closest);
      // Dark during a BURST-mode blackout slot, same as the beam itself.
      float gate = projectorGate(tb.x);
      float reveal = gate * (1.0 - smoothstep(0.0, uDustRadius, d));
      if (reveal > best) {
        best = reveal;
        bestColor = projectorColorD(tb.x);
      }
    }
    vReveal = best * uMasterGain;
    vColor = bestColor;

    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    float worldSize = ${DUST_POINT_WORLD_SIZE.toFixed(3)} + aSeed * ${DUST_POINT_WORLD_VARIANCE.toFixed(3)};
    gl_PointSize = clamp(worldSize * uPixelScale / max(-mv.z, 0.001), 1.0, uPointSizeMax);
    gl_Position = projectionMatrix * mv;
  }
`;

const dustFragmentShader = laserDustFragmentShader({ brightness: DUST_BRIGHTNESS, peakLuminance: PEAK_LUMINANCE });


// Hue distance on the colour wheel (shortest way round).
function hueDistance(a, b) {
  const d = Math.abs(a - b) % 1;
  return d > 0.5 ? 1 - d : d;
}

export class LaserCathedralVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;

    // Debug overrides, off unless explicitly asked for in the URL:
    //   ?lasermode=fan|cone|scan|wall|burst   pin one mode
    //   ?beams=N                              beams per projector
    //   ?laserpalette=1                       log every palette decision
    const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
    const MODE_NAMES = { fan: MODE_FAN, cone: MODE_CONE, scan: MODE_SCAN, wall: MODE_WALL, burst: MODE_BURST };
    const forced = params.get('lasermode');
    this._forcedMode = forced && MODE_NAMES[forced] !== undefined ? MODE_NAMES[forced] : null;
    const beams = parseInt(params.get('beams'), 10);
    this._forcedBeams = Number.isFinite(beams) && beams > 0 ? beams : null;
    this._debugPalette = params.get('laserpalette') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    // The haze is not decoration: a beam is only visible because there is
    // something in the air for it to scatter off. Owned here now that no rig
    // sets it up.
    scene.fog = new THREE.FogExp2(0x000000, FOG_DENSITY);
    // Just enough ambient for the floor to exist as a surface rather than as
    // void - without any light a MeshStandardMaterial floor renders pure
    // black and gives the impacts nothing to sit on.
    this._ambient = new THREE.AmbientLight(0x141a20, 0.85);
    scene.add(this._ambient);

    const floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: FLOOR_COLOR,
      roughness: 0.78,
      metalness: 0.0
    });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    scene.add(this._floor);

    const layout = TIER_LAYOUT[this._tier];
    this._perProjector = this._forcedBeams != null ? this._forcedBeams : layout.perProjector;
    // WALL/SCAN borrow extra slots from the SAME buffer rather than needing a
    // second geometry - see WALL_SCAN_BEAM_RATIO. Every other mode still uses
    // only this._perProjector of them (see the activeGate math the shaders
    // now carry), so FAN/CONE/BURST look exactly as before.
    this._perProjectorMax = Math.max(
      this._perProjector, Math.round(this._perProjector * WALL_SCAN_BEAM_RATIO));
    this._beamCount = this._perProjectorMax * PROJECTOR_COUNT;

    this._buildShared();
    this._buildBeams(layout);
    if (layout.sheet) this._buildSheet();
    if (layout.impacts) {
      this._buildImpacts();
      this._buildReflection();
    }
    this._buildSources();
    this._buildDust(layout);

    this._time = 0;
    this._mode = this._forcedMode != null ? this._forcedMode : MODE_FAN;
    // _buildShared() defaulted the uniform to FAN; a forced mode never goes
    // through _switchMode(), so without this it would never reach the shader.
    this._uniforms.uMode.value = this._mode;
    this._modeIndex = 0;
    this._beatCount = 0;
    this._lastBeatPhase = null;
    this._blackoutFrames = 0;
    this._burstEndsAtBeat = -1;
    // CHANTIER 6 (revision) - mode changes now fire on director.accentPhrase's
    // own rising edge (once per phrase) instead of a locally-rolled random
    // 8/16-beat timer - see update()'s own edge detection. _modeEndsAtTime
    // is the no-bpm fallback only (accentPhrase needs a beat grid).
    this._lastAccentPhrase = null;
    this._modeEndsAtTime = MODE_NO_BPM_SECONDS_MIN;
    this._lastPaletteRef = null;
    this._lastValence = null;
    this._scanNotch = 0;
    this._applyPalette({ artworkPalette: null, trackFeatures: null });
  }

  _buildShared() {
    // One set of holder objects shared by all three materials, so a uniform
    // written once in update() reaches the beams, the sheet and the impacts.
    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uMode: { value: MODE_FAN },
      uSpread: { value: 0.6 },
      uScanAngle: { value: 0 },
      uPulse: { value: 1 },
      uMasterGain: { value: 1 },
      uPixelScale: { value: 1 },
      uOuterPx: { value: BEAM_OUTER_PX },
      uCorePx: { value: BEAM_CORE_PX },
      uBeamsPerProjector: { value: this._perProjector },
      // WALL/SCAN's spread multiplier - see BEAM_DIR_CHUNK - 1 everywhere else.
      uWideBeams: { value: 1 },
      uDustRadius: { value: DUST_REVEAL_RADIUS },
      // WebGL's real point-size ceiling, read once - see the performance
      // contract's point-sprite clamp rule. Shared by the source and dust
      // materials below.
      uPointSizeMax: { value: Math.max(1, getMaxPointSize()) },
      // Fixed screen size for the projector glow points - canvasPixelHeight /
      // 1080, written per frame in update(). Deliberately NOT depth-divided
      // (see sourceVertexShader's own comment).
      uSourceSizeScale: { value: 1 },
      uColor0: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uColor1: { value: new THREE.Color(FALLBACK_COLORS[1]) },
      uColor2: { value: new THREE.Color(FALLBACK_COLORS[2]) },
      uProjector0: { value: new THREE.Vector3().fromArray(PROJECTORS[0]) },
      uProjector1: { value: new THREE.Vector3().fromArray(PROJECTORS[1]) },
      uProjector2: { value: new THREE.Vector3().fromArray(PROJECTORS[2]) }
    });
  }

  // A quad whose x is the side sign (-1/+1) and y the distance along the
  // beam (0..1) - the vertex shader turns that into real world geometry.
  _quadGeometry() {
    const positions = new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geometry;
  }

  _buildBeams(layout) {
    const count = this._beamCount;
    const projectors = new Float32Array(count);
    const indices = new Float32Array(count);
    const seeds = new Float32Array(count);
    let i = 0;
    for (let p = 0; p < PROJECTOR_COUNT; p++) {
      for (let b = 0; b < this._perProjectorMax; b++) {
        projectors[i] = p;
        indices[i] = b;
        seeds[i] = Math.random() * 10;
        i++;
      }
    }

    const geometry = this._quadGeometry();
    geometry.setAttribute('iProjector', new THREE.InstancedBufferAttribute(projectors, 1));
    geometry.setAttribute('iIndex', new THREE.InstancedBufferAttribute(indices, 1));
    geometry.setAttribute('iSeed', new THREE.InstancedBufferAttribute(seeds, 1));
    geometry.instanceCount = count;
    // Placement is entirely shader-side, so the mesh's own bounds are
    // meaningless to the frustum culler.
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 2, 0), 60);
    this._beamGeometry = geometry;

    this._beamMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: beamVertexShader,
      fragmentShader: beamFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this._beams = new THREE.Mesh(geometry, this._beamMaterial);
    this._scene.add(this._beams);
  }

  _buildSheet() {
    const projectors = new Float32Array([0, 1, 2]);
    const geometry = this._quadGeometry();
    geometry.setAttribute('iProjector', new THREE.InstancedBufferAttribute(projectors, 1));
    geometry.instanceCount = PROJECTOR_COUNT;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 2, 0), 60);
    this._sheetGeometry = geometry;

    this._sheetMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: sheetVertexShader,
      fragmentShader: sheetFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this._sheet = new THREE.Mesh(geometry, this._sheetMaterial);
    this._scene.add(this._sheet);
  }

  _buildImpacts() {
    const count = this._beamCount;
    const projectors = new Float32Array(count);
    const indices = new Float32Array(count);
    const seeds = new Float32Array(count);
    let i = 0;
    for (let p = 0; p < PROJECTOR_COUNT; p++) {
      for (let b = 0; b < this._perProjectorMax; b++) {
        projectors[i] = p;
        indices[i] = b;
        seeds[i] = Math.random() * 10;
        i++;
      }
    }
    // A flat unit quad in the XZ plane: x and z run -1..1, y is always 0, so
    // the vertex shader can lay it straight down on the floor.
    const quad = new Float32Array([-1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1]);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    geometry.setAttribute('iProjector', new THREE.InstancedBufferAttribute(projectors, 1));
    geometry.setAttribute('iIndex', new THREE.InstancedBufferAttribute(indices, 1));
    geometry.setAttribute('iSeed', new THREE.InstancedBufferAttribute(seeds, 1));
    geometry.instanceCount = count;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 60);
    this._impactGeometry = geometry;

    this._impactMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: impactVertexShader,
      fragmentShader: impactFragmentShader,
      transparent: true,
      depthWrite: false,
      // A pool of light is drawn ON the floor and must never be occluded by
      // it. Everything here is additive, so render order does not matter.
      depthTest: false,
      // The quad lies flat in the XZ plane and its winding puts the front
      // face DOWNWARD, so a camera above the floor only ever sees its back:
      // with the default FrontSide every pool was silently culled.
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this._impacts = new THREE.Mesh(geometry, this._impactMaterial);
    this._scene.add(this._impacts);
  }

  // Shares the impact geometry outright (identical iProjector/iIndex/iSeed
  // mapping - the reflection is the exact same set of floor hits, just drawn
  // stretched and dim) rather than rebuilding an equivalent buffer.
  _buildReflection() {
    this._reflectionMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: reflectionVertexShader,
      fragmentShader: reflectionFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this._reflection = new THREE.Mesh(this._impactGeometry, this._reflectionMaterial);
    this._scene.add(this._reflection);
  }

  // Three glow points (one per projector), combined core+halo in a single
  // draw call. Position is the projector's own static world coordinate -
  // there is no need to look it up in the shader via a uniform the way the
  // beams do, since these three points never move.
  _buildSources() {
    const positions = new Float32Array(PROJECTOR_COUNT * 3);
    const indices = new Float32Array(PROJECTOR_COUNT);
    for (let i = 0; i < PROJECTOR_COUNT; i++) {
      positions[i * 3] = PROJECTORS[i][0];
      positions[i * 3 + 1] = PROJECTORS[i][1];
      positions[i * 3 + 2] = PROJECTORS[i][2];
      indices[i] = i;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('iIndex', new THREE.BufferAttribute(indices, 1));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 6, -7), 12);
    this._sourceGeometry = geometry;

    this._sourceMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: sourceVertexShader,
      fragmentShader: sourceFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending
    });
    this._sources = new THREE.Points(geometry, this._sourceMaterial);
    this._scene.add(this._sources);
  }

  _buildDust(layout) {
    const count = layout.dustCount;
    const positions = new Float32Array(count * 3);
    const seeds = new Float32Array(count);
    const f = DUST_FIELD;
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() * 2 - 1) * f.halfX;
      positions[i * 3 + 1] = f.yMin + Math.random() * (f.yMax - f.yMin);
      positions[i * 3 + 2] = f.zMin + Math.random() * (f.zMax - f.zMin);
      seeds[i] = Math.random();
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 3, -2), 16);
    this._dustGeometry = geometry;

    this._dustMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: dustVertexShader,
      fragmentShader: dustFragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._dust = new THREE.Points(geometry, this._dustMaterial);
    this._scene.add(this._dust);
  }

  // Three projector colours as an ANALOGOUS triad anchored on the cover.
  //
  // The previous scheme picked the three most hue-distant vivid colours and,
  // whenever they were not far enough apart, threw the album away entirely for
  // fixed RGB primaries. Most sleeves are chromatically grouped, so that
  // fallback fired constantly and every show looked identical.
  //
  // Instead: anchor on the hue of the MOST SATURATED vivid colour (that is the
  // one carrying the cover's visual identity - vivid[0] is just whichever came
  // first), measure how much hue spread the cover actually has, and place the
  // three heads at H-delta, H, H+delta. The show stays recognisably the
  // album's colour family while the three projectors stay clearly separate.
  //
  // Crossings stay safe: an analogous triad shares the same deficient channel
  // across all three colours, so overlaps drift toward a neighbouring hue
  // rather than to white. The white-out this code once guarded against came
  // from wide triads like yellow/cyan/magenta, where every colour already
  // holds two channels at full - not from this shape.
  _applyPalette(profile) {
    const palette = profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const out = [this._uniforms.uColor0.value, this._uniforms.uColor1.value, this._uniforms.uColor2.value];
    const hsl = { h: 0, s: 0, l: 0 };
    const tmp = new THREE.Color();

    // True fallback only: no artwork at all, or an artwork that yielded no
    // colour whatsoever. A merely monochrome sleeve is the common case and
    // must still drive the show.
    if (vivid.length === 0 && dominant.length === 0) {
      for (let i = 0; i < 3; i++) {
        tmp.set(FALLBACK_COLORS[i]);
        tmp.getHSL(hsl);
        out[i].setHSL(hsl.h, Math.max(hsl.s, LASER_MIN_SATURATION),
          THREE.MathUtils.clamp(hsl.l, LASER_MIN_LIGHTNESS, LASER_MAX_LIGHTNESS));
      }
      if (this._debugPalette) {
        console.log('[laser-palette] no colour at all -> FALLBACK_COLORS');
      }
      return;
    }

    // Anchor: the most saturated vivid colour, else the first dominant one.
    const source = vivid.length > 0 ? vivid : dominant;
    let anchorH = 0, anchorS = 0, anchorL = 0.5, bestSat = -1;
    const hues = [];
    for (let i = 0; i < source.length; i++) {
      tmp.set(source[i]);
      tmp.getHSL(hsl);
      hues.push(hsl.h);
      if (hsl.s > bestSat) { bestSat = hsl.s; anchorH = hsl.h; anchorS = hsl.s; anchorL = hsl.l; }
    }

    // How much hue spread the cover genuinely has - the widest circular gap
    // between any two of its colours.
    let spread = 0;
    for (let i = 0; i < hues.length; i++) {
      for (let j = i + 1; j < hues.length; j++) spread = Math.max(spread, hueDistance(hues[i], hues[j]));
    }
    const delta = THREE.MathUtils.clamp(spread / 2, TRIAD_DELTA_MIN, TRIAD_DELTA_MAX);

    // A laser is saturated, but a pastel sleeve and a neon one should not
    // produce the same show - so these are floors and a range, not overrides.
    const sat = Math.max(anchorS, LASER_MIN_SATURATION);
    const light = THREE.MathUtils.clamp(anchorL, LASER_MIN_LIGHTNESS, LASER_MAX_LIGHTNESS);
    const triad = [(anchorH - delta + 1) % 1, anchorH, (anchorH + delta) % 1];
    for (let i = 0; i < 3; i++) out[i].setHSL(triad[i], sat, light);

    if (this._debugPalette) {
      const hex = (c) => '#' + c.getHexString();
      console.log('[laser-palette]',
        'vivid=', vivid.map((c) => '#' + c.toString(16).padStart(6, '0')).join(','),
        '| dominant=', dominant.map((c) => '#' + c.toString(16).padStart(6, '0')).join(','),
        '| anchorH=', anchorH.toFixed(3), 'anchorS=', anchorS.toFixed(3), 'anchorL=', anchorL.toFixed(3),
        '| spread=', spread.toFixed(3), 'delta=', delta.toFixed(3),
        '| triad=', triad.map((h) => h.toFixed(3)).join(','),
        '| out=', out.map(hex).join(','),
        '| branch=', vivid.length > 0 ? 'vivid-anchored' : 'dominant-anchored');
    }
  }

  // Hard cut, preceded by a 2-frame blackout. No crossfade: a real laser
  // show never fades between looks.
  _switchMode() {
    if (Math.random() < BURST_PROBABILITY && this._mode !== MODE_BURST) {
      this._mode = MODE_BURST;
      this._burstEndsAtBeat = this._beatCount + BURST_BEATS;
    } else {
      this._modeIndex = (this._modeIndex + 1) % CYCLE_MODES.length;
      this._mode = CYCLE_MODES[this._modeIndex];
      this._burstEndsAtBeat = -1;
    }
    this._uniforms.uMode.value = this._mode;
    this._blackoutFrames = BLACKOUT_FRAMES;
    this._modeEndsAtTime = this._time + MODE_NO_BPM_SECONDS_MIN + Math.random() * (MODE_NO_BPM_SECONDS_MAX - MODE_NO_BPM_SECONDS_MIN);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    // Freezing _time freezes every beam where it stands - directions are all
    // derived from it, so nothing drifts while paused.
    if (!paused) this._time += deltaTime;
    this._uniforms.uTime.value = this._time;

    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    const hasBeat = !paused && profile.beatPhase != null;

    // Rising edge of beatPhase - the only beat signal available.
    if (hasBeat) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) {
        this._beatCount++;
        // SCAN advances one quantised notch per beat, never continuously -
        // that stepping is what reads as a real scanner rather than an
        // animation.
        this._scanNotch++;
        this._uniforms.uScanAngle.value = Math.sin(this._scanNotch * 0.7) * SCAN_STEP_RAD * 6;
      }
      this._lastBeatPhase = profile.beatPhase;
    } else {
      this._lastBeatPhase = null;
      // No tempo: the scan sweeps continuously instead of stepping, since
      // there are no beats to step on.
      if (!paused) this._uniforms.uScanAngle.value = Math.sin(this._time * 0.55) * SCAN_STEP_RAD * 6;
    }

    if (!paused) {
      const burstOver = this._mode === MODE_BURST && this._beatCount >= this._burstEndsAtBeat;
      // CHANTIER 6 (revision) - director.accentPhrase's own rising edge
      // (once every 16-beat phrase, in its last 2 beats) drives the mode
      // change now, replacing this file's own locally-rolled 8/16-beat
      // timer - see director.js. Falls back to a plain wall clock when
      // there's no bpm (accentPhrase needs a beat grid it can't have then).
      const accentPhrase = profile.director ? profile.director.accentPhrase : null;
      const accentRisingEdge = accentPhrase === 1 && this._lastAccentPhrase !== 1;
      this._lastAccentPhrase = accentPhrase;
      const dueOnAccent = bpm && accentRisingEdge;
      const dueOnClock = !bpm && this._time >= this._modeEndsAtTime;
      if (this._forcedMode == null && (burstOver || dueOnAccent || dueOnClock)) this._switchMode();
    }

    if (this._blackoutFrames > 0) {
      this._blackoutFrames--;
      this._uniforms.uMasterGain.value = 0;
    } else {
      this._uniforms.uMasterGain.value = 1;
    }

    // WALL and SCAN are the two gesture types that otherwise leave the frame
    // emptiest - borrow the extra buffer slots (see _perProjectorMax) and
    // widen their own spread so they actually reach the frame's edges. Every
    // other mode keeps exactly its original beam count and spread.
    const isWideMode = this._mode === MODE_WALL || this._mode === MODE_SCAN;
    this._uniforms.uBeamsPerProjector.value = isWideMode ? this._perProjectorMax : this._perProjector;
    this._uniforms.uWideBeams.value = isWideMode ? WALL_SCAN_WIDE_MULTIPLIER : 1;

    if (profile.artworkPalette !== this._lastPaletteRef ||
        (profile.trackFeatures && profile.trackFeatures.valence) !== this._lastValence) {
      this._lastPaletteRef = profile.artworkPalette;
      this._lastValence = profile.trackFeatures && profile.trackFeatures.valence;
      this._applyPalette(profile);
    }

    // Narrow at the very start/end of a track, wide through the middle.
    const intensity = profile.director.intensity;
    this._uniforms.uSpread.value = 0.32 + intensity * 0.78;
    this._uniforms.uPulse.value = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);

    // Pixels-per-world-unit at unit depth, for the beams' screen-space width
    // floor. The impacts no longer need it: they are world-sized quads now,
    // scaled by the projection like any other geometry.
    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    this._uniforms.uPixelScale.value = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));
    this._uniforms.uSourceSizeScale.value = canvasPixelHeight / 1080;
  }

  dispose() {
    this._scene.fog = null;
    this._scene.remove(this._ambient);
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._scene.remove(this._beams);
    this._beamGeometry.dispose();
    this._beamMaterial.dispose();
    if (this._sheet) {
      this._scene.remove(this._sheet);
      this._sheetGeometry.dispose();
      this._sheetMaterial.dispose();
    }
    if (this._impacts) {
      this._scene.remove(this._impacts);
      this._impactGeometry.dispose();
      this._impactMaterial.dispose();
    }
    if (this._reflection) {
      this._scene.remove(this._reflection);
      // Geometry is owned by (and disposed with) _impacts above - this mesh
      // only ever borrowed it.
      this._reflectionMaterial.dispose();
    }
    this._scene.remove(this._sources);
    this._sourceGeometry.dispose();
    this._sourceMaterial.dispose();
    this._scene.remove(this._dust);
    this._dustGeometry.dispose();
    this._dustMaterial.dispose();
  }
}
